'use client';

import { memo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Auth } from '@air/api';

import { type OAuthCustomState } from '~/components/auth/types';
import { Button, type ButtonVariants } from '~/components/Button';

export type GoogleSignInButtonProps = {
  /** Plugin-side context to round-trip through Cognito to the post-auth
   * callback page. See `OAuthCustomState` for the supported fields. */
  customState?: OAuthCustomState;
  /** Callback to navigate to OAuth waiting page with the generated id */
  onNavigate: (id: string) => void;
  pluginName: string;
  /** Button size — defaults to 'large' for backwards compatibility */
  size?: ButtonVariants['size'];
};

export const GoogleSignInButton = memo(
  ({ customState, onNavigate, pluginName, size = 'large' }: GoogleSignInButtonProps) => {
    const handleClick = useCallback(() => {
      const id = uuidv4();

      // Spread customState first so caller fields can't shadow `id` or
      // `pluginName` — `onNavigate(id)` polls against the locally generated
      // UUID and a shadowed id would silently desync auth from polling.
      Auth.googleSignIn({ ...customState, id, pluginName });

      onNavigate(id);
    }, [customState, onNavigate, pluginName]);

    return (
      <Button appearance="outline" onClick={handleClick} prefix={<GoogleIcon />} size={size}>
        Log in with Google
      </Button>
    );
  },
);

GoogleSignInButton.displayName = 'GoogleSignInButton';

const GoogleIcon = memo(() => (
  <svg aria-hidden="true" className="size-5" viewBox="0 0 24 24">
    <path
      d="m6.99 13.877-.626 2.338-2.288.05A8.906 8.906 0 0 1 3 12c0-1.494.362-2.9 1.005-4.138l2.04.373.893 2.025a5.361 5.361 0 0 0 .052 3.617Z"
      fill="#FBBB00"
    />
    <path
      d="M20.842 10.32a8.99 8.99 0 0 1-.039 3.561 9.01 9.01 0 0 1-3.167 5.14l-2.567-.13-.362-2.268a5.366 5.366 0 0 0 2.306-2.739h-4.816V10.32h8.645Z"
      fill="#518EF8"
    />
    <path
      d="M17.636 19.017A8.958 8.958 0 0 1 12 21a8.997 8.997 0 0 1-7.928-4.735l2.918-2.388a5.352 5.352 0 0 0 7.713 2.743l2.933 2.397Z"
      fill="#28B446"
    />
    <path
      d="M17.745 5.07 14.83 7.459A5.311 5.311 0 0 0 12 6.649a5.36 5.36 0 0 0-5.062 3.61l-2.933-2.4A9.002 9.002 0 0 1 12 3c2.183 0 4.187.777 5.745 2.07Z"
      fill="#F14336"
    />
  </svg>
));

GoogleIcon.displayName = 'GoogleIcon';
